import safeStringify from "fast-safe-stringify";

import { GetStaticPaths, GetStaticProps, NextPage } from "next";

import { useEffect } from "react";

import { ContentfulNavigation } from "../../@types/contentfulNavigation";
import { IPageFields } from "../../@types/generated/contentful";
import { getHero } from "../../app/[slug]/component";
import ContentSectionWrapper from "../../components/content-section-wrapper";
import CountdownBar from "../../components/countdown-bar";
import PageLayout from "../../components/page/layout";
import getContentfulContentTypeEntries from "../../utils/contentful/getContentfulContentTypeEntries";
import getContentfulPage from "../../utils/contentful/getContentfulPage";
import generateTitle from "../../utils/page/generateTitle";
import getJointPageProperties, {
  JointPageProperties,
} from "../../utils/page/getJointPageProperties";
import { BAS_KAST_TENANT_NAME } from "../../utils/tenant-names";
import { isBasKastTenantPage } from "../../utils/tenants";

interface PageProps {
  isPreview?: boolean;
  page: IPageFields;
  previewData?: any;
  navigation: ContentfulNavigation;
  jointPageProperties: JointPageProperties;
}

/**
 * Top level page
 * @param {PageProps} props
 * @param {IPageFields} props.page
 * @returns JSX.Element
 */
const Page: NextPage<PageProps> = ({
  isPreview,
  page,
  previewData,
  navigation,
  jointPageProperties,
}: PageProps) => {
  const {
    contentSections,
    heroTeaser,
    seoMetadata,
    countdownPage,
    noNavigation,
    tenant,
  } = page;
  const {
    fields: { title },
  } = seoMetadata;
  const { footerProps } = jointPageProperties;
  const {
    logoDark,
    logoLight,
    titleSuffix,
    bannerVisible,
    bannerText,
    bannerIcon,
  } = jointPageProperties.siteSettings;

  useEffect(() => {
    if (isPreview && previewData?.internal !== "") {
      const previewElement = document.getElementById(previewData.internal);
      if (previewElement) {
        previewElement.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [isPreview, previewData.internal]);

  const fullTitle = generateTitle(title, titleSuffix);

  return (
    <PageLayout
      title={fullTitle}
      footer={footerProps}
      logoHeader={logoDark}
      logoFooter={logoLight}
      navigation={navigation}
      bannerVisible={bannerVisible}
      bannerText={bannerText}
      bannerIcon={bannerIcon}
      metadata={seoMetadata.fields}
      noNavigation={noNavigation}
      tenant={tenant}
    >
      {heroTeaser && getHero(heroTeaser, contentSections)}
      {contentSections?.map((section, i) => (
        <ContentSectionWrapper
          key={`section-${i}`}
          section={section}
        ></ContentSectionWrapper>
      ))}
      {countdownPage && <CountdownBar {...countdownPage.fields}></CountdownBar>}
    </PageLayout>
  );
};

export default Page;

/**
 * This function gets called at build time to pre-fetch the routes of the course pages
 * @returns {Promise<{paths: {params: {slug: string}}[], fallback: boolean}>}
 */
export const getStaticPaths: GetStaticPaths = async () => {
  // Call contentful API endpoint to get course pages
  const pages = await getContentfulContentTypeEntries<IPageFields>({
    contentType: "page",
    filter: {
      "fields.tenant": "baskast",
      "fields.isRecipe[ne]": "true",
    },
  }).then((entries) => entries.items.map((entry) => entry.fields));

  // Filter out tenant pages
  const filteredPages = pages.filter((page) => isBasKastTenantPage(page));

  // Get the paths we want to pre-render based on the pages
  const paths = filteredPages.map((page) => ({
    params: {
      slug: page.slug,
    },
  }));

  // We'll pre-render only these paths at build time.
  // { fallback: false } means other routes should 404.
  return { paths, fallback: false };
};

/**
 * This function gets called at build time on server-side to statically pre-fetch the data for the page
 * @param {GetStaticPropsContext<ParsedUrlQuery, PreviewData>} context
 * @returns page data
 */
export const getStaticProps: GetStaticProps<PageProps> = async (context) => {
  const [page, jointPageProperties] = await Promise.all([
    getContentfulPage({
      // get page slug from context
      slug: context.params?.slug?.toString() || "",
      isPreview: context.preview || false,
      previewData: context.previewData,
    }),
    getJointPageProperties(context, BAS_KAST_TENANT_NAME),
  ]);

  // if no page was found, return 404
  if (!page) {
    return { notFound: true };
  }

  const propsData = safeStringify({
    page: page.fields,
    isPreview: context.preview || false,
    previewData: context.previewData || {
      env: process.env.NEXT_PUBLIC_CONTENTFUL_ENV,
      internal: "",
    },
    navigation: jointPageProperties.navigation,
    jointPageProperties,
    theme: BAS_KAST_TENANT_NAME,
  });

  const props = JSON.parse(propsData);

  // pass page data to the page via props
  return {
    props,
  };
};
